import auth from "services/account";
import * as types from "../mutation-types";

const state = {
  isLoggedIn: null,
  account: {},
  token: null,
  message: null,
  status: null,
  pending: false,
  registerPending: false,
  registerStatus: null,
  emailNotVerified: false,
  emailNotVerifiedAddress: null,
  // Impersonation state
  isImpersonating: false,
  originalUser: null,
  impersonationStartedAt: null,
  impersonationReturnUrl: null
};

const mutations = {
  [types.LOGIN](state) {
    state.pending = true;
  },

  [types.LOGIN_SUCCESS](state) {
    state.isLoggedIn = true;
    state.pending = false;
    state.message = "Authenticated";
  },
  [types.LOGOUT](state) {
    state.isLoggedIn = false;
    state.token = "";
    state.account = {};
  },
  [types.ACCOUNT_MSG](state, payload) {
    state.message = payload.statusText;
    if (payload.data && payload.data.message) {
      state.message = payload.data.message;
    }
    state.status = payload.status;
    state.pending = false;

    // Stocker les infos email_not_verified
    if (payload.data && payload.data.email_not_verified) {
      state.emailNotVerified = true;
      state.emailNotVerifiedAddress = payload.data.email;
    } else {
      state.emailNotVerified = false;
      state.emailNotVerifiedAddress = null;
    }
  },
  [types.REGISTER](state, payload) {
    state.registerPending = true;
  },
  [types.REGISTERED](state, payload) {
    state.registerStatus = payload.status;
    if (payload.status_code) {
      state.registerStatus = payload.status_code;
    }

    state.registerPending = false;
  },
  [types.SAVING_ACCOUNT](state) {
    state.pending = true;
  },
  [types.SAVED_ACCOUNT](state, payload) {
    state.account = payload;
    state.pending = false;
  },

  [types.SET_ACCOUNT](state, payload) {
    state.token = payload.meta.token;
    state.account = payload.user;
  },
  [types.REFRESH_ACCOUNT](state, payload) {
    //state.token = payload.meta.token
    state.account = payload;
  },
  setRefreshedAccount(state, payload) {
    //state.token = payload.meta.token
    state.account = payload;
  },

  // Impersonation mutations
  [types.START_IMPERSONATION](state, payload) {
    // Update Vuex state - vuex-persistedstate will automatically sync to localStorage
    state.isImpersonating = true;
    state.isLoggedIn = true;
    state.originalUser = payload.meta.original_user;
    state.impersonationStartedAt = new Date().toISOString();
    state.token = payload.meta.token;
    state.account = payload.data;

    // Also store token directly for axios interceptor
    localStorage.setItem('token', payload.meta.token);
  },

  [types.STOP_IMPERSONATION](state, payload) {
    // Mettre à jour l'état local
    state.isImpersonating = false;
    state.originalUser = null;
    state.impersonationStartedAt = null;
    
    // Vérifier où se trouve le token
    const token = payload.meta?.token || payload.token;
    if (!token) {
      console.error('STOP_IMPERSONATION: Aucun token trouvé dans le payload!');
      return;
    }
    
    state.token = token;
    state.account = payload.data || payload.user || {};
    
    // Sauvegarder le nouveau token
    localStorage.setItem('token', token);
    
    // Nettoyer les clés d'impersonation individuelles
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('originalUser');
  },

  [types.SET_IMPERSONATION_STATUS](state, payload) {
    state.isImpersonating = payload.is_impersonating;
    if (payload.is_impersonating && payload.original_user) {
      state.originalUser = payload.original_user;
      state.impersonationStartedAt = payload.started_at ? new Date(payload.started_at * 1000).toISOString() : null;
    } else {
      state.originalUser = null;
      state.impersonationStartedAt = null;
    }
  },

  // Mutation pour forcer le nettoyage complet de l'état d'impersonation
  FORCE_CLEAN_IMPERSONATION(state) {
    // Nettoyer uniquement les données d'impersonation
    state.isImpersonating = false;
    state.originalUser = null;
    state.impersonationStartedAt = null;
    
    // Nettoyer le localStorage
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('originalUser');
  },
};

const getters = {
  isLoggedIn: state => state.isLoggedIn,
  emailNotVerified: state => state.emailNotVerified,
  emailNotVerifiedAddress: state => state.emailNotVerifiedAddress,
  getAccount: state => state.account,
  getToken: state => state.token,
  getRoles: state => state.account.roles,
  getStatus: state => state.status,
  currentUser: state => state.account || {},
  hasPermission: state => permission => {
    if (!state.account || !state.account.can) {
      return false;
    }
    return state.account.can[permission] === true;
  },
  // Impersonation getters
  isImpersonating: state => {
    // Vérifier d'abord le token JWT qui est la source de vérité
    const token = localStorage.getItem('token');
    if (!token) {
      return false;
    }
    
    try {
      // Décoder le payload du JWT
      const parts = token.split('.');
      if (parts.length !== 3) return false;
      
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const payload = JSON.parse(jsonPayload);
      return payload.impersonating === true;
    } catch (e) {
      // En cas d'erreur de décodage, on n'est pas en impersonation
      return false;
    }
  },
  originalUser: state => {
    // Si on est en impersonation, retourner l'utilisateur original depuis l'état
    const token = localStorage.getItem('token');
    if (!token) return null;
    
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(window.atob(base64));
      if (payload.impersonating && state.originalUser) {
        return state.originalUser;
      }
    } catch (e) {
      // Ignorer les erreurs
    }
    return null;
  },
  impersonationStartedAt: state => state.impersonationStartedAt,
  impersonationReturnUrl: state => state.impersonationReturnUrl || '/admin',
  canImpersonate: state => {
    if (!state.account || !state.account.can) {
      return false;
    }
    return state.account.can['impersonate users'] === true;
  },
  canImpersonateCandidates: state => {
    if (!state.account || !state.account.can) {
      return false;
    }
    return state.account.can['impersonate candidates'] === true;
  }
};

const actions = {
  async checkAccount({ commit }) {
    commit(types.SAVING_ACCOUNT);
    const payload = await auth.checkIsAdmin();
    if ((await payload.status) === 200) {
      commit(types.REFRESH_ACCOUNT, await payload.data.user);
      if (!_.isEmpty(await payload.data.user.candidates)) {
        commit("candidate/SET_CANDIDATE", await payload.data.user, {
          root: true
        });
      }
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async register({ commit }, credentials) {
    commit(types.REGISTER);
    commit("candidate/SET_CANDIDATE", {}, { root: true });
    const payload = await auth.register(credentials);
    commit(types.REGISTERED, await payload);

    // Vérifier si l'email doit être vérifié (statut 201)
    if ((await payload.status) === 201 && payload.data && payload.data.email_verification_required) {
      commit(types.ACCOUNT_MSG, await payload);
      return payload;
    }

    if ((await payload.status) === 200) {
      commit(types.SET_ACCOUNT, await payload.data);

      if (!_.isEmpty(await payload.data.user.candidates)) {
        commit("candidate/SET_CANDIDATE", await payload.data.user, {
          root: true
        });
      } else {
        commit("candidate/RESET_CANDIDATE", await payload.data, { root: true });
      }
      commit(types.LOGIN_SUCCESS);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async shopRegister({ commit }, credentials) {
    commit(types.REGISTER);
    commit("candidate/SET_CANDIDATE", {}, { root: true });
    const payload = await auth.shopRegister(credentials);
    commit(types.REGISTERED, await payload);

    // Vérifier si l'email doit être vérifié (statut 201)
    if ((await payload.status) === 201 && payload.data && payload.data.email_verification_required) {
      commit(types.ACCOUNT_MSG, await payload);
      return payload;
    }

    if ((await payload.status) === 200) {
      commit(types.SET_ACCOUNT, await payload.data);
      commit(types.LOGIN_SUCCESS);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async adminRegister({ commit }, credentials) {
    commit(types.REGISTER);
    const payload = await auth.adminRegister(credentials);
    commit(types.REGISTERED, await payload);
    commit(types.ACCOUNT_MSG, await payload);
  },
  async login({ commit }, credentials) {
    commit(types.LOGIN);
    commit("candidate/SET_CANDIDATE", {}, { root: true });
    const payload = await auth.login(credentials);

    if ((await payload.status) === 200) {
      commit(types.SET_ACCOUNT, await payload.data);
      if (!_.isEmpty(await payload.data.user.candidates)) {
        commit("candidate/SET_CANDIDATE", await payload.data.user, {
          root: true
        });
      }
      commit(types.LOGIN_SUCCESS);
    }

    commit(types.ACCOUNT_MSG, await payload);
  },
  async logout({ commit }) {
    const payload = (await auth.logout()).data;
    commit(types.LOGOUT);
    commit(types.ACCOUNT_MSG, await payload);
    commit("orders/resetOrders", null, { root: true });
  },
  async lostPassword({ commit }, email) {
    commit(types.LOGIN);
    const payload = await auth.lostPassword(email);
    commit(types.ACCOUNT_MSG, await payload);
  },
  async resetPassword({ commit }, credentials) {
    commit(types.LOGIN);
    const payload = await auth.resetPassword(credentials);
    commit(types.REFRESH_ACCOUNT, await payload.data.user);
    commit(types.ACCOUNT_MSG, await payload);
  },
  async setAccount({ commit }) {
    try {
      const payload = await auth.refresh();
      commit(types.REFRESH_ACCOUNT, await payload.data);
      if (!_.isEmpty(await payload.data.candidates)) {
        commit("candidate/SET_CANDIDATE", await payload.data, { root: true });
      } else {
        commit("candidate/RESET_CANDIDATE", await payload.data, { root: true });
      }
      commit(types.LOGIN_SUCCESS);
    } catch (e) {
      commit(types.LOGOUT);
      commit("candidate/SET_CANDIDATE", {}, { root: true });
    }
  },
  async saveAccount({ commit }, account) {
    commit(types.SAVING_ACCOUNT);
    const payload = await auth.saveAccount(account);
    if ((await payload.status) === 200) {
      commit(types.SAVED_ACCOUNT, await payload.data);
      if (!_.isEmpty(await payload.data.candidates)) {
        commit("candidate/SET_CANDIDATE", await payload.data, { root: true });
      }
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async updateAccount({ commit }, account) {
    commit(types.SAVING_ACCOUNT);
    const payload = await auth.updateAccount(account);
    if ((await payload.status) === 200) {
      commit(types.SAVED_ACCOUNT, await payload.data);
      if (!_.isEmpty(await payload.data.candidates)) {
        commit("candidate/SET_CANDIDATE", await payload.data, { root: true });
      }
    }
    commit(types.ACCOUNT_MSG, await payload);
  },

  // Impersonation actions
  async startImpersonation({ commit }, userId) {
    try {
      const payload = await auth.impersonateUser(userId);
      if (payload.status === 200) {
        commit(types.START_IMPERSONATION, payload.data);
        return payload;
      }
      commit(types.ACCOUNT_MSG, payload);
      throw new Error('Impersonation failed');
    } catch (error) {
      commit(types.ACCOUNT_MSG, error.response);
      throw error;
    }
  },

  async stopImpersonation({ commit }) {
    try {
      const payload = await auth.stopImpersonation();
      
      if (payload.status === 200) {
        commit(types.STOP_IMPERSONATION, payload.data);
        return payload;
      }
      commit(types.ACCOUNT_MSG, payload);
      throw new Error('Stop impersonation failed');
    } catch (error) {
      commit(types.ACCOUNT_MSG, error.response);
      throw error;
    }
  },

  async checkImpersonationStatus({ commit, dispatch }) {
    try {
      const payload = await auth.getImpersonationStatus();
      if (payload.status === 200) {
        const impersonationData = payload.data.data;
        commit(types.SET_IMPERSONATION_STATUS, impersonationData);
        
        // Si on n'est plus en impersonation, nettoyer complètement
        if (!impersonationData.is_impersonating) {
          // Utiliser la mutation de nettoyage forcé
          commit('FORCE_CLEAN_IMPERSONATION');
        }
        
        return payload;
      }
    } catch (error) {
      // En cas d'erreur, nettoyer tout
      commit(types.SET_IMPERSONATION_STATUS, { is_impersonating: false });
      commit('FORCE_CLEAN_IMPERSONATION');
      
      console.warn('Could not check impersonation status:', error);
    }
  },

  async initializeFromStorage({ commit }) {
    // Check localStorage for impersonation state on app initialization
    const isImpersonating = localStorage.getItem('isImpersonating') === 'true';
    const originalUser = localStorage.getItem('originalUser');
    
    if (isImpersonating && originalUser) {
      try {
        commit(types.SET_IMPERSONATION_STATUS, {
          is_impersonating: true,
          original_user: JSON.parse(originalUser)
        });
      } catch (e) {
        // Si erreur de parsing, nettoyer localStorage
        localStorage.removeItem('isImpersonating');
        localStorage.removeItem('originalUser');
        commit(types.SET_IMPERSONATION_STATUS, {
          is_impersonating: false
        });
      }
    } else {
      // S'assurer que l'état est propre
      commit(types.SET_IMPERSONATION_STATUS, {
        is_impersonating: false
      });
    }
  },

  forceCleanImpersonation({ commit }) {
    commit('FORCE_CLEAN_IMPERSONATION');
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
